import { IDispatchId, IRequestError } from '../../utils/helpers/redux.helper';
import { StatementInfo } from './models/statement-info.model';
import { StatementMetrics } from './models/statement-metrics.model';
import { IStatementData, IStatementMetricParams } from './statement-data.data';

// action payload interfaces
export interface IClientId {
  clientId: string;
}

export interface IRequestSuccess extends IClientId, IDispatchId {}

export interface IStatementRequestError extends IClientId, IRequestError {}

export interface IFetchStatementInfoPayload extends IDispatchId {
  clientId: string;
  startDate: string;
  endDate: string;
  unpaidOnly: boolean;
}

export interface IRefreshStatementInfo extends IDispatchId, IClientId {}

export interface IFetchStatementMetricsPayload extends IDispatchId, IClientId {
  params: IStatementMetricParams;
}

export interface IUpdateStatementInfoPayload extends IClientId {
  statementInfo: StatementInfo;
}

export interface IUpdateStatementMetricsPayload extends IClientId {
  statementMetrics: StatementMetrics;
}

export interface ISendStatementPayload extends IDispatchId, IClientId {
  statementData: IStatementData;
}

// action enums
export enum ActionsEnum {
  FETCH_STATEMENT_INFO = 'FETCH_STATEMENT_INFO',
  FETCH_STATEMENT_INFO_SUCCESS = 'FETCH_STATEMENT_INFO_SUCCESS',
  FETCH_STATEMENT_INFO_ERROR = 'FETCH_STATEMENT_INFO_ERROR',
  REFRESH_STATEMENT_INFO = 'REFRESH_STATEMENT_INFO',
  UPDATE_STATEMENT_INFO = 'UPDATE_STATEMENT_INFO',
  FETCH_STATEMENT_METRICS = 'FETCH_STATEMENT_METRICS',
  FETCH_STATEMENT_METRICS_SUCCESS = 'FETCH_STATEMENT_METRICS_SUCCESS',
  FETCH_STATEMENT_METRICS_ERROR = 'FETCH_STATEMENT_METRICS_ERROR',
  REFRESH_STATEMENT_METRICS = 'REFRESH_STATEMENT_METRICS',
  UPDATE_STATEMENT_METRICS = 'UPDATE_STATEMENT_METRICS',
  SEND_STATEMENT = 'SEND_STATEMENT',
  SEND_STATEMENT_SUCCESS = 'SEND_STATEMENT_SUCCESS',
  SEND_STATEMENT_ERROR = 'SEND_STATEMENT_ERROR',
}

// action definitions
export interface FetchStatementInfo {
  type: ActionsEnum.FETCH_STATEMENT_INFO;
  payload: IFetchStatementInfoPayload;
}

export interface FetchStatementInfoSuccess {
  type: ActionsEnum.FETCH_STATEMENT_INFO_SUCCESS;
  payload: IRequestSuccess;
}

export interface FetchStatementInfoError {
  type: ActionsEnum.FETCH_STATEMENT_INFO_ERROR;
  payload: IStatementRequestError;
}

export interface RefreshStatementInfo {
  type: ActionsEnum.REFRESH_STATEMENT_INFO;
  payload: IRefreshStatementInfo;
}

export interface UpdateStatementInfo {
  type: ActionsEnum.UPDATE_STATEMENT_INFO;
  payload: IUpdateStatementInfoPayload;
}

export interface FetchStatementMetrics {
  type: ActionsEnum.FETCH_STATEMENT_METRICS;
  payload: IFetchStatementMetricsPayload;
}

export interface FetchStatementMetricsSuccess {
  type: ActionsEnum.FETCH_STATEMENT_METRICS_SUCCESS;
  payload: IRequestSuccess;
}

export interface FetchStatementMetricsError {
  type: ActionsEnum.FETCH_STATEMENT_METRICS_ERROR;
  payload: IStatementRequestError;
}

export interface UpdateStatementMetrics {
  type: ActionsEnum.UPDATE_STATEMENT_METRICS;
  payload: IUpdateStatementMetricsPayload;
}

export interface SendStatement {
  type: ActionsEnum.SEND_STATEMENT;
  payload: ISendStatementPayload;
}

export interface SendStatementSuccess {
  type: ActionsEnum.SEND_STATEMENT_SUCCESS;
  payload: IRequestSuccess;
}

export interface SendStatementError {
  type: ActionsEnum.SEND_STATEMENT_ERROR;
  payload: IStatementRequestError;
}

// action creators
export const fetchStatementInfo = (
  dispatchId: string,
  clientId: string,
  startDate: string,
  endDate: string,
  unpaidOnly: boolean
): FetchStatementInfo => ({
  type: ActionsEnum.FETCH_STATEMENT_INFO,
  payload: {
    dispatchId,
    clientId,
    startDate,
    endDate,
    unpaidOnly,
  },
});

export const fetchStatementInfoSuccess = (
  dispatchId: string,
  clientId: string
): FetchStatementInfoSuccess => ({
  type: ActionsEnum.FETCH_STATEMENT_INFO_SUCCESS,
  payload: {
    dispatchId,
    clientId,
  },
});

export const fetchStatementInfoError = (
  dispatchId: string,
  clientId: string,
  error: Error
): FetchStatementInfoError => ({
  type: ActionsEnum.FETCH_STATEMENT_INFO_ERROR,
  payload: {
    dispatchId,
    clientId,
    error,
  },
});

export const refreshStatementInfo = (
  dispatchId: string,
  clientId: string
): RefreshStatementInfo => ({
  type: ActionsEnum.REFRESH_STATEMENT_INFO,
  payload: {
    dispatchId,
    clientId,
  },
});

export const updateStatementInfo = (
  clientId: string,
  statementInfo: StatementInfo
): UpdateStatementInfo => ({
  type: ActionsEnum.UPDATE_STATEMENT_INFO,
  payload: {
    clientId,
    statementInfo,
  },
});

export const fetchStatementMetrics = (
  dispatchId: string,
  clientId: string,
  params: IStatementMetricParams
): FetchStatementMetrics => ({
  type: ActionsEnum.FETCH_STATEMENT_METRICS,
  payload: {
    dispatchId,
    clientId,
    params,
  },
});

export const fetchStatementMetricsSuccess = (
  dispatchId: string,
  clientId: string
): FetchStatementMetricsSuccess => ({
  type: ActionsEnum.FETCH_STATEMENT_METRICS_SUCCESS,
  payload: {
    dispatchId,
    clientId,
  },
});

export const fetchStatementMetricsError = (
  dispatchId: string,
  clientId: string,
  error: Error
): FetchStatementMetricsError => ({
  type: ActionsEnum.FETCH_STATEMENT_METRICS_ERROR,
  payload: {
    dispatchId,
    clientId,
    error,
  },
});

export const updateStatementMetrics = (
  clientId: string,
  statementMetrics: StatementMetrics
): UpdateStatementMetrics => ({
  type: ActionsEnum.UPDATE_STATEMENT_METRICS,
  payload: {
    clientId,
    statementMetrics,
  },
});

export const sendStatement = (
  dispatchId: string,
  clientId: string,
  statementData: IStatementData
): SendStatement => ({
  type: ActionsEnum.SEND_STATEMENT,
  payload: {
    dispatchId,
    clientId,
    statementData,
  },
});

export const sendStatementSuccess = (
  dispatchId: string,
  clientId: string
): SendStatementSuccess => ({
  type: ActionsEnum.SEND_STATEMENT_SUCCESS,
  payload: {
    dispatchId,
    clientId,
  },
});

export const sendStatementError = (
  dispatchId: string,
  clientId: string,
  error: Error
): SendStatementError => ({
  type: ActionsEnum.SEND_STATEMENT_ERROR,
  payload: {
    dispatchId,
    clientId,
    error,
  },
});
